/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Fab,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { makeStyles } from '@material-ui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvancedSearch from 'modules/common/advancedSearch.component';
import RecurringPayableAttachmentModal from 'modules/common/RecurringPayableAttachmentModal.component';
import { CreateExpenseCodeSearchInfo } from 'modules/common/expenseCodeHelper';
import { formatDateForInput } from 'utilities/common';
import { ExceptionId } from 'modules/authorizations/details/notesAndCommunicationEditableCard.component';
import SelectedCommentAndNotesComponent from 'modules/authorizations/details/commentsAndNotes/selectedCommentAndNotes.component';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';

const useStyles = makeStyles(() => ({
  mb2: { marginBottom: '4px', marginRight: '16px' },
  iconOverlayImg: { position: 'absolute', zIndex: '1 !important' }
}));

const RecurringPayableReportLineItem = ({
  lineItem,
  expenseCodes = [],
  authorizationSummaries = [],
  rpInfoForm,
  isReadOnly = false,
  onUpdateLine,
  setFormLineItems,
  closeLineItem,
  payToVendor,
  updateParentRPAttachments,
  updateParentRPLineAttachments,
  exceptions,
  setExceptions,
  enumerations,
  clientId
}) => {
  const classes = useStyles();

  const codeSearchInfo = useMemo(() => CreateExpenseCodeSearchInfo(expenseCodes), [expenseCodes]);

  const [form, setForm] = useState({ ...lineItem });
  const [original, setOriginal] = useState({ ...lineItem });
  const [isSaving, setIsSaving] = useState(false);
  const [isOnHold, setIsOnHold] = useState(!!(lineItem && lineItem.onHoldDate));

  const selectedExpenseLineItem =
    exceptions && Array.isArray(exceptions) ? exceptions.find(e => e.exceptionId === lineItem.authorizationExceptionId) : null;

    let filteredExceptions =
    exceptions && Array.isArray(exceptions)
      ? exceptions.filter(es => isIdEqual(es.exceptionStatus, 'D71921DD-72F0-44E3-9F1D-ED9CF71CD285'))
      : [];

  const [openAddException, setOpenAddException] = useState(false);

  const validSelections =
    filteredExceptions && Array.isArray(filteredExceptions)
      ? filteredExceptions.filter(s => s.authorizationId === lineItem.paymentAuthorizationId)
      : [];

  const [selectedExceptionId, setSelectedExceptionId] = useState(selectedExpenseLineItem?.exceptionId ?? 0);

  useEffect(() => {
    setForm({ ...lineItem });
    setIsOnHold(!!(lineItem && lineItem.onHoldDate));
  }, [lineItem]);

  const ecById = id => expenseCodes.find(e => Number(e.expenseCodeId) === Number(id));
  const categoryLabel = id => (ecById(id)?.category ? ecById(id).category : '—');

  const codeDisplayFromId = id => {
    const ec = ecById(id);
    if (!ec) return '';
    const code = ec.expenseCodeCode || ec.expenseCode || '';
    const desc = ec.expenseCodeDescription || ec.description || '';
    return desc ? `${code} – ${desc}` : code;
  };

  const calculateTotalAmount = value => {
    const next = value === '' ? 0 : Number(value);
    setForm(prev => ({ ...prev, totalAmount: next }));
    const id = lineItem.lineItemId;
    setFormLineItems(prev =>
      Array.isArray(prev)
        ? prev.map(it => (it && it.lineItemId === id ? { ...it, totalAmount: value === '' ? '' : Number(value) } : it))
        : prev
    );
  };

  const handleSave = async () => {
    setIsSaving(true);

    const payload = {
      lineItemId: form.lineItemId || null,
      recurringPayableId: form.recurringPayableId || rpInfoForm?.recurringPayableInformation?.recurringPayableId || null,
      lineItemStatusId: form.lineItemStatusId || null,
      expenseCodeId: form.expenseCodeId != null ? Number(form.expenseCodeId) : null,
      description: form.description || '',
      payOption: form.payOption == null ? null : !!form.payOption,
      totalAmount: form.totalAmount === '' ? null : Number(form.totalAmount),
      approvedAmount: form.approvedAmount === '' ? null : form.approvedAmount != null ? Number(form.approvedAmount) : null,
      referenceString: form.referenceString || null,
      glNumber: form.glNumber || null,
      deniedReason: form.deniedReason || null,
      reportDate: form.reportDate || null,
      payrollDate: form.payrollDate || null,
      dueDate: form.dueDate || null,
      fromDate: form.fromDate || null,
      toDate: form.toDate || null,
      entryDate: form.entryDate || null,
      expenseDate: form.expenseDate || null,
      paymentAuthorizationId: form.paymentAuthorizationId || null,
      onHoldDate: isOnHold ? form.onHoldDate || new Date().toISOString() : null,
      authorizationExceptionId: selectedExceptionId === 0 ? null : selectedExceptionId === '' ? null : selectedExceptionId
    };

    const ok = await onUpdateLine(payload);
    if (ok) {
      setIsSaving(false);
      closeLineItem();
      return;
    }

    const id = original.lineItemId;
    setFormLineItems(prev => (Array.isArray(prev) ? prev.map(it => (it && it.lineItemId === id ? { ...original } : it)) : prev));
    setForm({ ...original });
    setIsOnHold(!!original.onHoldDate);
    setIsSaving(false);
  };

  const handleCancel = () => {
    const id = original.lineItemId;
    setFormLineItems(prev => (Array.isArray(prev) ? prev.map(it => (it && it.lineItemId === id ? { ...original } : it)) : prev));
    setForm({ ...original });
    setIsOnHold(!!original.onHoldDate);
    setOriginal(null);
    closeLineItem();
  };

  return (
    <Box>
      <Grid container spacing={1} columns={20}>
        <Grid item md={3}>
          {payToVendor ? (
            <AdvancedSearch
              labelText="Authorization"
              displayValueAccessorKey="transfereeName"
              saveIdValueAccessorKey="fileId"
              searchingCriteria={[
                { name: 'fileId', title: 'Auth Id' },
                { name: 'transfereeName', title: 'Full Name' },
                { name: 'departure', title: 'Departure' },
                { name: 'destination', title: 'Destination' },
                { name: 'clientName', title: 'Client' }
              ]}
              searchingCriteriaExtensions={[
                { columnName: 'fileId', width: 150 },
                { columnName: 'transfereeName', width: 250 },
                { name: 'departure', width: 175 },
                { name: 'destination', width: 175 },
                { columnName: 'clientName', width: 175 }
              ]}
              searchingRows={authorizationSummaries}
              id={form.paymentAuthorizationId}
              value={(authorizationSummaries.find(a => a.fileId === form.paymentAuthorizationId) || {}).transfereeName || ''}
              setIdValue={id => setForm(s => ({ ...s, paymentAuthorizationId: id }))}
              isReadOnly={isReadOnly}
            />
          ) : (
            <TextField
              disabled
              value={(authorizationSummaries.find(a => a.fileId === form.paymentAuthorizationId) || {}).transfereeName || ''}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          )}
        </Grid>

        <Grid item md={1}>
          <Box sx={{ pt: '10px' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {categoryLabel(form.expenseCodeId)}
            </Typography>
          </Box>
        </Grid>

        <Grid item md={3}>
          {(() => {
            const info = codeSearchInfo;
            return (
              <AdvancedSearch
                labelText=""
                displayValueAccessorKey={info.nameKey}
                saveIdValueAccessorKey={info.idKey}
                searchingCriteria={info.searchingColumns}
                searchingCriteriaExtensions={info.searchingColumnExtensions}
                searchingRows={info.searchingRows}
                id={form.expenseCodeId}
                value={codeDisplayFromId(form.expenseCodeId)}
                setIdValue={(id, displayValue) => {
                  const ec = ecById(id);
                  setForm(s => ({
                    ...s,
                    expenseCodeId: id,
                    expenseCode: displayValue,
                    cat: ec && ec.category ? ec.category : s.cat
                  }));
                }}
                maxWidthsx="360px"
                isReadOnly={isReadOnly}
              />
            );
          })()}
        </Grid>

        <Grid item md={2}>
          <TextField
            value={form.totalAmount == null ? '' : form.totalAmount}
            fullWidth
            type="number"
            InputLabelProps={{ shrink: true }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            disabled={isReadOnly}
            onChange={e => calculateTotalAmount(e.target.value)}
          />
        </Grid>

        <Grid item md={5}>
          <TextField
            value={form.description || ''}
            fullWidth
            disabled={isReadOnly}
            InputLabelProps={{ shrink: true }}
            onChange={e => setForm(s => ({ ...s, description: e.target.value }))}
          />
        </Grid>

        <Grid item md={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isOnHold}
                disabled={isReadOnly}
                onChange={() => {
                  setIsOnHold(v => !v);
                  setForm(s => ({ ...s, onHoldDate: s.onHoldDate ? null : new Date().toISOString() }));
                }}
              />
            }
            label="On Hold"
          />
        </Grid>

        <Grid item md={2}>
          <RecurringPayableAttachmentModal
            displayText={isReadOnly ? 'View Attachments' : 'Add Attachment'}
            isReadOnly={isReadOnly}
            recurringPayableLineInfo={rpInfoForm.recurringPayableInformation}
            recurringPayableAttachments={rpInfoForm.recurringPayableAttachments}
            recurringPayableLineAttachments={
              rpInfoForm && rpInfoForm.recurringPayableLineAttachments
                ? rpInfoForm.recurringPayableLineAttachments.filter(rla => rla.recurringPayableLineItemId === lineItem.lineItemId)
                : []
            }
            updateParentRPAttachments={updateParentRPAttachments}
            updateParentRPLineAttachments={updateParentRPLineAttachments}
            lineItemId={lineItem.lineItemId}
          />
        </Grid>

        <Grid item md={1}>
          <Fab className={classes.iconOverlayImg} color="primary" size="small" onClick={handleCancel}>
            <FontAwesomeIcon size="1x" icon={['fas', 'xmark']} color="#fff" />
          </Fab>
        </Grid>
        <Grid item md={2}>
          <Typography className={classes.mb2} variant="subtitle2" sx={{ color: '#777667' }}>
            Approved Amount
          </Typography>
          <TextField
            value={form.approvedAmount == null ? '' : form.approvedAmount}
            fullWidth
            type="number"
            InputLabelProps={{ shrink: true }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            disabled={isReadOnly}
            onChange={e => setForm(s => ({ ...s, approvedAmount: e.target.value }))}
          />
        </Grid>

        <Grid item md={2}>
          <Typography className={classes.mb2} variant="subtitle2" sx={{ color: '#777667' }}>
            Pay Option
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!form.payOption}
                disabled={isReadOnly}
                onChange={e => setForm(s => ({ ...s, payOption: e.target.checked }))}
              />
            }
            label="Enabled"
          />
        </Grid>

        <Grid item md={4}>
          <Typography className={classes.mb2} variant="subtitle2" sx={{ color: '#777667' }}>
            Reference
          </Typography>
          <TextField
            value={form.referenceString || ''}
            fullWidth
            disabled={isReadOnly}
            onChange={e => setForm(s => ({ ...s, referenceString: e.target.value }))}
          />
        </Grid>

        <Grid item md={4}>
          <Typography className={classes.mb2} variant="subtitle2" sx={{ color: '#777667' }}>
            GL Number
          </Typography>
          <TextField
            value={form.glNumber || ''}
            fullWidth
            disabled={isReadOnly}
            onChange={e => setForm(s => ({ ...s, glNumber: e.target.value }))}
          />
        </Grid>

        <Grid item md={8}>
          <Typography className={classes.mb2} variant="subtitle2" sx={{ color: '#777667' }}>
            Denied Reason
          </Typography>
          <TextField
            value={form.deniedReason || ''}
            fullWidth
            disabled={isReadOnly}
            onChange={e => setForm(s => ({ ...s, deniedReason: e.target.value }))}
          />
        </Grid>

        {['reportDate', 'payrollDate', 'dueDate', 'fromDate', 'toDate', 'entryDate', 'expenseDate'].map(k => (
          <Grid key={k} item md={2}>
            <Typography className={classes.mb2} variant="subtitle2" sx={{ color: '#777667' }}>
              {k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
            </Typography>
            <TextField
              type="date"
              value={form[k] ? formatDateForInput(form[k]) : ''}
              fullWidth
              disabled={isReadOnly}
              onChange={e => setForm(s => ({ ...s, [k]: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        ))}

        <Grid item md={2}>
          <Box>
            <Typography className={classes.mb2} variant="subtitle2" sx={{ color: '#777667' }}>
              Exception
            </Typography>
            <Select
              labelId={'selectedException'}
              key={'selectedException'}
              name={'selectedException'}
              value={selectedExceptionId}
              sx={{ maxWidth: '140px', minWidth: '120px' }}
              disabled={isReadOnly}
              onChange={e => {
                let id = e.target.value;
                setSelectedExceptionId(id);
              }}
            >
              <MenuItem disabled={isReadOnly} key={0} value={0} sx={{ margin: 'dense' }}>
                None
              </MenuItem>
              {validSelections.map(opt => (
                <MenuItem disabled={isReadOnly} key={opt.exceptionId} value={opt.exceptionId} sx={{ margin: 'dense' }}>
                  {opt.commentSubject}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>
        <Grid item md={4} sx={{ marginTop: 4 }}>
          {!isReadOnly && (
            <Button variant="contained" color="primary" onClick={() => setOpenAddException(true)} sx={{ minWidth: 120 }}>
              Add Exception
            </Button>
          )}
        </Grid>
        <Grid item md={20} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 8, mt: 2 }}>
          <Button variant="contained" onClick={handleCancel} sx={{ minWidth: 120 }}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={isReadOnly || isSaving} sx={{ minWidth: 120 }}>
            Save Line
          </Button>
          <Dialog open={openAddException} onClose={() => setOpenAddException(false)} fullWidth maxWidth="md">
            <DialogTitle>Add Exception</DialogTitle>
            <DialogContent dividers>
              <SelectedCommentAndNotesComponent
                isNewComment={true}
                selectedComment={{
                  authorizationCommentId: null,
                  taskId: null,
                  isTransfereeViewable: false,
                  isClientViewable: false,
                  isPinned: false,
                  commentId: null,
                  serviceId: null,
                  regardingId: null,
                  subject: '',
                  comment: '',
                  addedByUserId: null,
                  modifiedByUserId: null,
                  createdParty: null,
                  isException: true,
                  authorizationExceptionCommentId: null,
                  exceptionReason: null,
                  requestedAmount: null,
                  statusId: null,
                  exceptionCodeId: null,
                  categoryId: null,
                  requestTypeId: null,
                  approvedById: null,
                  approvedDate: null,
                  approvedAmount: null,
                  exceptionTypeId: null,
                  requestedItem: null,

                  addedOn: null,
                  templateId: null,
                  emailTemplateSentDate: null,
                  exceptionApprovers: [],
                  exceptionLineItems: []
                }}
                halfView={false}
                enumerations={enumerations}
                defaultEmail={''}
                clientContacts={[]}
                setIsLoading={() => {}}
                setSelectedComment={() => {}}
                isAuthLineItem={false}
                onAfterCreate={(newExceptionCommentId, approved) => {
                  if (approved) {
                    setSelectedExceptionId(newExceptionCommentId);
                  }
                  setOpenAddException(false);
                }}
                forceRegardingId={ExceptionId}
                clientId={clientId}
                authorizationId={lineItem.paymentAuthorizationId}
                setExceptionState={setExceptions}
              />
            </DialogContent>
          </Dialog>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RecurringPayableReportLineItem;
